/**
 * SDK Client Initialization
 * Rule: Single SDK instance for the entire application.
 * Integration instances are constructed once in integrations-registry.js and re-exported here.
 */
import { ASSET_INVENTORY_PAGE_KEY } from '@repo/contracts';
import { skipNextRefetchForEntity } from '@/hooks/useRefetchListOnReferenceVersion.js';
import { wireReferenceTableLocate } from '@/lib/notifications/wire-reference-table-locate.js';
import { wireListNotificationLocate } from '@/lib/notifications/wire-list-notification-locate.js';
// PageApprovalConfig / PendingChange integrations removed (Plan Faz 1.7).




// PendingApproval integration removed (Plan Faz 1.7). Replaced by WorkflowTask integration (Plan Faz 3, ADR-002).
import { attachReferenceTableContract } from '../services/legacy-reference-table-bridge.js';
import { createSimpleTableIntegration } from '../services/simple-reference-table.integration.js';
// AutomationDefinition integration removed (Plan Faz 1.7). Replaced by WorkflowTemplate + workflow-engine (ADR-002 / ADR-006).
import {
    useRegulatorySourceTableStore,
    useRegulatoryClauseTableStore,
    useComplianceObligationTableStore,
    useComplianceFrameworkTableStore,
    useDpiaTemplateTableStore,
    useIncidentTemplateTableStore,
    useVendorAssessmentTableStore,
    useGovernanceReportTableStore,
    useReportConfidentialityClassTableStore,
    useRetentionPeriodTableStore,
    useInternationalTransferTableStore,
    useTransferTypeTableStore,
    useRecipientGroupTableStore,
    useDataDisposalMethodTableStore,
    useDataSourceTableStore,
    usePrivacyPrincipleTableStore,
    useSecurityMeasureTableStore,
    useDutyDefinitionTableStore,
} from '@/store/simple-reference-table.store.js';
import { hydrationManager } from '../services/reference-hydration.js';
import { useReferenceStore } from '@/store/reference.store.js';
import { getEntityStrategy, LOAD_STRATEGIES } from '@/config/reference.config.js';
import { sdk } from './sdk-instance.js';
import {
    applicationMethodIntegration,
    applicationRequestInformationIntegration,
    applicationTrackingIntegration,
    assessmentIntegration,
    assessmentQuestionIntegration,
    assetCategoryIntegration,
    assetGroupCriticalityEvaluationIntegration,
    assetGroupIntegration,
    assetGroupMeasureApplicabilityIntegration,
    assetLifecycleIntegration,
    assetsIntegration,
    assignmentTemplateIntegration,
    auditAssignmentIntegration,
    auditEvaluationIntegration,
    auditFindingIntegration,
    auditPlanIntegration,
    authIntegration,
    backupIntegration,
    bcmAppDetailsIntegration,
    bcmInitialResponseIntegration,
    bcmRpoIntegration,
    bcmSettingsIntegration,
    bcpIntegration,
    bcpLocationIntegration,
    biaImpactConfigurationIntegration,
    biaIntegration,
    bigdesCompensatoryControlIntegration,
    bigdesImplementationProcessEvaluationIntegration,
    bigdesMeasureIntegration,
    bigdesRoadMapWorkPackageIntegration,
    businessCalendarIntegration,
    changeRequestIntegration,
    classificationIntegration,
    collectionMethodIntegration,
    communicationChannelIntegration,
    communicationIntegration,
    communicationPlanIntegration,
    communicationProviderIntegration,
    companyIntegration,
    competenceIntegration,
    consentRecordIntegration,
    contextIntegration,
    controlIntegration,
    cookieInventoryIntegration,
    correctiveActionIntegration,
    crisisEventIntegration,
    crisisImprovementActionsIntegration,
    crisisRecoveryActivitiesIntegration,
    crisisRecoveryActivityIntegration,
    currentSituationEvaluationIntegration,
    customerImportIntegration,
    dashboardIntegration,
    dataBreachIntegration,
    dataDisposalMethodIntegration,
    dataSourceIntegration,
    dataSubjectRequestIntegration,
    deadLetterIntegration,
    delegationIntegration,
    detailedReportProfileIntegration,
    detailedReportingIntegration,
    disposalIntegration,
    documentIntegration,
    documentRevisionIntegration,
    documentTypeIntegration,
    dpaIntegration,
    dpiaAssessmentIntegration,
    entityEventIntegration,
    eventObservabilityIntegration,
    evidenceBundlesIntegration,
    exerciseEvaluationIntegration,
    exerciseImprovementActionsIntegration,
    exerciseParticipantIntegration,
    exerciseRecoveryActivityIntegration,
    exercisesIntegration,
    explicitConsentIntegration,
    explicitConsentMethodIntegration,
    explicitConsentReasonIntegration,
    formSubmissionIntegration,
    formTaskIntegration,
    formTemplateIntegration,
    formsIntegration,
    gapIntegration,
    gdprDashboardIntegration,
    homeIntegration,
    incidentIntegration,
    integrationConnectionIntegration,
    integrationExportIntegration,
    interestedPartyIntegration,
    itsmIntegration,
    kpiIntegration,
    kvkkCompanyIntegration,
    legalReasonIntegration,
    lessonLearnedIntegration,
    lifecycleIntegration,
    mailTemplateIntegration,
    matrixConfigurationIntegration,
    meetingIntegration,
    myWorkIntegration,
    notificationIntegration,
    notificationPreferenceIntegration,
    notificationRoutingIntegration,
    notificationTemplateIntegration,
    objectiveIntegration,
    operationalExecutionIntegration,
    outboxMonitorIntegration,
    partyInstanceIntegration,
    partyTemplateIntegration,
    patchNotesIntegration,
    pdInventoryIntegration,
    personalDataCategoryIntegration,
    privacyNoticeIntegration,
    privacyPrincipleIntegration,
    problemIntegration,
    processIntegration,
    processingPurposeIntegration,
    queueObservabilityIntegration,
    recipientGroupIntegration,
    recoveryResourcesCrudIntegration,
    recoveryResourcesIntegration,
    reportArchiveIntegration,
    reportArtifactIntegration,
    reportBrandingIntegration,
    reportProfileIntegration,
    reportRunsIntegration,
    reportSurfacesIntegration,
    reportingArchiveIntegration,
    retentionPeriodIntegration,
    riskAnalysisIntegration,
    riskConsequenceIntegration,
    riskIntegration,
    riskMonitoringIntegration,
    riskRegisterIntegration,
    riskReportsIntegration,
    riskTreatmentIntegration,
    roleIntegration,
    rootCauseIntegration,
    rpoCalculationIntegration,
    rpoRecommendationIntegration,
    scenarioTimelineIntegration,
    scopeDocumentIntegration,
    securityMeasureIntegration,
    seedPolicyIntegration,
    serviceCatalogItemIntegration,
    soaIntegration,
    strategicInitiativeIntegration,
    subjectPersonGroupIntegration,
    surveyDispatchIntegration,
    collectionCampaignIntegration,
    systemLogsIntegration,
    tailorabilityIntegration,
    teamIntegration,
    tenantComplianceIntegration,
    tenantDirectoryIntegration,
    identityProviderIntegration,
    tenantMfaSettingsIntegration,
    tenantOnboardingIntegration,
    threatIntegration,
    ticketCategoryIntegration,
    ticketConfigIntegration,
    ticketIntegration,
    ticketRecurrenceIntegration,
    ticketSlaIntegration,
    ticketTemplateIntegration,
    trainingIntegration,
    unitIntegration,
    userIntegration,
    userNavigationPreferenceIntegration,
    userTrainingIntegration,
    vendorIntegration,
    verbisIntegration,
    vulnerabilityIntegration,
    weaknessIntegration,
    webhookSubscriptionIntegration,
    workflowInstanceIntegration,
    workflowTaskIntegration,
    workflowTemplateIntegration
} from './integrations-registry.js';
// Re-export registry instances (pages/hooks import from client.js)
export {
    applicationMethodIntegration,
    applicationRequestInformationIntegration,
    applicationTrackingIntegration,
    assessmentIntegration,
    assessmentQuestionIntegration,
    assetCategoryIntegration,
    assetGroupCriticalityEvaluationIntegration,
    assetGroupIntegration,
    assetGroupMeasureApplicabilityIntegration,
    assetLifecycleIntegration,
    assetsIntegration,
    assignmentTemplateIntegration,
    auditAssignmentIntegration,
    auditEvaluationIntegration,
    auditFindingIntegration,
    auditPlanIntegration,
    authIntegration,
    backupIntegration,
    bcmAppDetailsIntegration,
    bcmInitialResponseIntegration,
    bcmRpoIntegration,
    bcmSettingsIntegration,
    bcpIntegration,
    bcpLocationIntegration,
    biaImpactConfigurationIntegration,
    biaIntegration,
    bigdesCompensatoryControlIntegration,
    bigdesImplementationProcessEvaluationIntegration,
    bigdesMeasureIntegration,
    bigdesRoadMapWorkPackageIntegration,
    businessCalendarIntegration,
    changeRequestIntegration,
    classificationIntegration,
    collectionMethodIntegration,
    communicationChannelIntegration,
    communicationIntegration,
    communicationPlanIntegration,
    communicationProviderIntegration,
    companyIntegration,
    competenceIntegration,
    consentRecordIntegration,
    contextIntegration,
    controlIntegration,
    cookieInventoryIntegration,
    correctiveActionIntegration,
    crisisEventIntegration,
    crisisImprovementActionsIntegration,
    crisisRecoveryActivitiesIntegration,
    crisisRecoveryActivityIntegration,
    currentSituationEvaluationIntegration,
    customerImportIntegration,
    dashboardIntegration,
    dataBreachIntegration,
    dataDisposalMethodIntegration,
    dataSourceIntegration,
    dataSubjectRequestIntegration,
    deadLetterIntegration,
    delegationIntegration,
    detailedReportProfileIntegration,
    detailedReportingIntegration,
    disposalIntegration,
    documentIntegration,
    documentRevisionIntegration,
    documentTypeIntegration,
    dpaIntegration,
    dpiaAssessmentIntegration,
    entityEventIntegration,
    eventObservabilityIntegration,
    evidenceBundlesIntegration,
    exerciseEvaluationIntegration,
    exerciseImprovementActionsIntegration,
    exerciseParticipantIntegration,
    exerciseRecoveryActivityIntegration,
    exercisesIntegration,
    explicitConsentIntegration,
    explicitConsentMethodIntegration,
    explicitConsentReasonIntegration,
    formSubmissionIntegration,
    formTaskIntegration,
    formTemplateIntegration,
    formsIntegration,
    gapIntegration,
    gdprDashboardIntegration,
    homeIntegration,
    incidentIntegration,
    integrationConnectionIntegration,
    integrationExportIntegration,
    interestedPartyIntegration,
    itsmIntegration,
    kpiIntegration,
    kvkkCompanyIntegration,
    legalReasonIntegration,
    lessonLearnedIntegration,
    lifecycleIntegration,
    mailTemplateIntegration,
    matrixConfigurationIntegration,
    meetingIntegration,
    myWorkIntegration,
    notificationIntegration,
    notificationPreferenceIntegration,
    notificationRoutingIntegration,
    notificationTemplateIntegration,
    objectiveIntegration,
    operationalExecutionIntegration,
    outboxMonitorIntegration,
    partyInstanceIntegration,
    partyTemplateIntegration,
    patchNotesIntegration,
    pdInventoryIntegration,
    personalDataCategoryIntegration,
    privacyNoticeIntegration,
    privacyPrincipleIntegration,
    problemIntegration,
    processIntegration,
    processingPurposeIntegration,
    queueObservabilityIntegration,
    recipientGroupIntegration,
    recoveryResourcesCrudIntegration,
    recoveryResourcesIntegration,
    reportArchiveIntegration,
    reportArtifactIntegration,
    reportBrandingIntegration,
    reportProfileIntegration,
    reportRunsIntegration,
    reportSurfacesIntegration,
    reportingArchiveIntegration,
    retentionPeriodIntegration,
    riskAnalysisIntegration,
    riskConsequenceIntegration,
    riskIntegration,
    riskMonitoringIntegration,
    riskRegisterIntegration,
    riskReportsIntegration,
    riskTreatmentIntegration,
    roleIntegration,
    rootCauseIntegration,
    rpoCalculationIntegration,
    rpoRecommendationIntegration,
    scenarioTimelineIntegration,
    scopeDocumentIntegration,
    securityMeasureIntegration,
    seedPolicyIntegration,
    serviceCatalogItemIntegration,
    soaIntegration,
    strategicInitiativeIntegration,
    subjectPersonGroupIntegration,
    surveyDispatchIntegration,
    collectionCampaignIntegration,
    systemLogsIntegration,
    tailorabilityIntegration,
    teamIntegration,
    tenantComplianceIntegration,
    tenantDirectoryIntegration,
    identityProviderIntegration,
    tenantMfaSettingsIntegration,
    tenantOnboardingIntegration,
    threatIntegration,
    ticketCategoryIntegration,
    ticketConfigIntegration,
    ticketIntegration,
    ticketRecurrenceIntegration,
    ticketSlaIntegration,
    ticketTemplateIntegration,
    trainingIntegration,
    unitIntegration,
    userIntegration,
    userNavigationPreferenceIntegration,
    userTrainingIntegration,
    vendorIntegration,
    verbisIntegration,
    vulnerabilityIntegration,
    weaknessIntegration,
    webhookSubscriptionIntegration,
    workflowInstanceIntegration,
    workflowTaskIntegration,
    workflowTemplateIntegration,
};


// Create Integration instances
// Workflow runtime uses workflowInstanceIntegration + workflowTaskIntegration + myWorkIntegration.
//
unitIntegration.locateRowInList = (params) => unitIntegration.locateUnitInList(params);

/** @deprecated Use pdInventoryIntegration */
export const personalDataInventoryIntegration = pdInventoryIntegration;
export const dutyDefinitionIntegration = createSimpleTableIntegration(
    sdk.dutyDefinition,
    useDutyDefinitionTableStore,
    {
        referenceEntity: 'dutyDefinition',
        messagesKeyPrefix: 'teamAssociation:messages',
        // updateSuccess is used for team assignment; duty CRUD uses updateSuccessRecord
        messageKeys: { update: 'updateSuccessRecord' },
    },
);
export const reportConfidentialityClassIntegration = createSimpleTableIntegration(
    sdk.reportConfidentialityClasses,
    useReportConfidentialityClassTableStore,
);
export const regulatorySourceIntegration = createSimpleTableIntegration(sdk.regulatorySource, useRegulatorySourceTableStore, { referenceEntity: 'regulatorySource', translationKeyPrefix: 'definitionsNav:regulatorySources.form.notifications' });
export const regulatoryClauseIntegration = createSimpleTableIntegration(sdk.regulatoryClause, useRegulatoryClauseTableStore, { referenceEntity: 'regulatoryClause', translationKeyPrefix: 'definitionsNav:regulatoryClauses.form.notifications' });
export const complianceObligationIntegration = createSimpleTableIntegration(sdk.complianceObligation, useComplianceObligationTableStore, { referenceEntity: 'regulatoryObligation', translationKeyPrefix: 'definitionsNav:regulatoryObligations.form.notifications' });
export const complianceFrameworkIntegration = createSimpleTableIntegration(sdk.complianceFramework, useComplianceFrameworkTableStore, { referenceEntity: 'complianceFramework', translationKeyPrefix: 'definitionsNav:complianceFrameworks.form.notifications' });
export const dpiaTemplateIntegration = createSimpleTableIntegration(
    sdk.dpiaTemplate,
    useDpiaTemplateTableStore,
    {
        referenceEntity: 'privacyDpiaTemplate',
        translationKeyPrefix: 'definitionsNav:privacyDpiaTemplates.form.notifications',
    },
);
export const incidentTemplateIntegration = createSimpleTableIntegration(
    sdk.incidentTemplate,
    useIncidentTemplateTableStore,
    { referenceEntity: 'incidentTemplate', messagesKeyPrefix: 'incidentIncidents:templates.messages' },
);
export const vendorAssessmentIntegration = createSimpleTableIntegration(sdk.vendorAssessment, useVendorAssessmentTableStore, { referenceEntity: 'vendorAssessment', translationKeyPrefix: 'definitionsNav:vendorAssessments.form.notifications' });
export const governanceReportDefinitionIntegration = createSimpleTableIntegration(
    sdk.governanceReportDefinition,
    useGovernanceReportTableStore,
    { referenceEntity: 'governanceReportDefinition', translationKeyPrefix: 'definitionsNav:governanceReportDefinitions.form.notifications' },
);
// automationDefinitionIntegration removed (Plan Faz 1.7). Replaced by workflowTemplateIntegration (ADR-002).
export const privacyRetentionTableIntegration = createSimpleTableIntegration(
    sdk.retentionPeriod,
    useRetentionPeriodTableStore,
    {
        referenceEntity: 'privacyRetentionPeriod',
        translationKeyPrefix: 'definitionsNav:privacyRetentionPeriods.form.notifications',
    },
);
export const internationalTransferTableIntegration = createSimpleTableIntegration(
    sdk.internationalTransfer,
    useInternationalTransferTableStore,
    {
        referenceEntity: 'internationalTransfer',
        messagesKeyPrefix: 'kvkkInternationalTransfer:messages',
    },
);
export const transferTypeTableIntegration = createSimpleTableIntegration(
    sdk.transferType,
    useTransferTypeTableStore,
    {
        referenceEntity: 'transferType',
        messagesKeyPrefix: 'kvkkTransferType:messages',
    },
);
export const recipientGroupTableIntegration = createSimpleTableIntegration(
    sdk.recipientGroup,
    useRecipientGroupTableStore,
    {
        referenceEntity: 'recipientGroup',
        messagesKeyPrefix: 'kvkkSimpleDefinitions:recipientGroup.messages',
    },
);
export const dataDisposalMethodTableIntegration = createSimpleTableIntegration(
    sdk.dataDisposalMethod,
    useDataDisposalMethodTableStore,
    {
        referenceEntity: 'dataDisposalMethod',
        messagesKeyPrefix: 'kvkkSimpleDefinitions:dataDisposalMethod.messages',
    },
);
export const dataSourceTableIntegration = createSimpleTableIntegration(
    sdk.dataSource,
    useDataSourceTableStore,
    {
        referenceEntity: 'dataSource',
        messagesKeyPrefix: 'kvkkSimpleDefinitions:dataSource.messages',
    },
);
export const privacyPrincipleTableIntegration = createSimpleTableIntegration(
    sdk.privacyPrinciple,
    usePrivacyPrincipleTableStore,
    {
        referenceEntity: 'privacyPrinciple',
        messagesKeyPrefix: 'kvkkSimpleDefinitions:privacyPrinciple.messages',
    },
);
export const securityMeasureTableIntegration = createSimpleTableIntegration(
    sdk.securityMeasure,
    useSecurityMeasureTableStore,
    {
        referenceEntity: 'securityMeasure',
        messagesKeyPrefix: 'kvkkSimpleDefinitions:securityMeasure.messages',
    },
);

// ── Reference Data (Global Cache) ─────────────────────────────────────────────
hydrationManager.init(sdk.referenceData);

function attachReferenceMutationRefresh(integration, entity) {
    if (!integration || !entity) return integration;
    const proto = Object.getPrototypeOf(integration);
    if (!proto) return integration;

    const methodNames = Object.getOwnPropertyNames(proto).filter((name) => {
        if (name === 'constructor') return false;
        const fn = integration[name];
        if (typeof fn !== 'function') return false;
        return /^(create|update|delete|remove|bulk|transfer)/i.test(name);
    });

    methodNames.forEach((name) => {
        const original = integration[name].bind(integration);
        integration[name] = async (...args) => {
            const result = await original(...args);
            // Only after successful mutation — failed calls must not orphan skip tokens.
            if (result?.success !== false) {
                skipNextRefetchForEntity(entity);
                const strategy = getEntityStrategy(entity);
                if (
                    strategy === LOAD_STRATEGIES.RESOLVE_ONLY
                    || strategy === LOAD_STRATEGIES.REMOTE_SEARCH
                ) {
                    useReferenceStore.getState().bumpEntityVersion(entity);
                } else {
                    hydrationManager.refreshEntity(entity, { force: true });
                }
            }
            return result;
        };
    });

    return integration;
}

attachReferenceTableContract(collectionMethodIntegration, {
    fetch: 'fetchCollectionMethods',
    create: 'createCollectionMethod',
    update: 'updateCollectionMethod',
    remove: 'deleteCollectionMethod',
});
attachReferenceTableContract(legalReasonIntegration, {
    fetch: 'fetchLegalReasons',
    create: 'createLegalReason',
    update: 'updateLegalReason',
    remove: 'deleteLegalReason',
});
attachReferenceTableContract(processingPurposeIntegration, {
    fetch: 'fetchPurposes',
    create: 'createPurpose',
    update: 'updatePurpose',
    remove: 'deletePurpose',
});
attachReferenceTableContract(personalDataCategoryIntegration, {
    create: 'create',
    update: 'update',
    remove: 'delete',
});
personalDataCategoryIntegration.fetchList = (page, pageSize, search, sortBy, sortOrder, columnFilters, extraQuery) =>
    personalDataCategoryIntegration.fetchAll({
        page,
        pageSize,
        search,
        sortBy,
        sortOrder,
        ...columnFilters,
        ...extraQuery,
    });
attachReferenceTableContract(subjectPersonGroupIntegration, {
    fetch: 'fetchGroups',
    create: 'createGroup',
    update: 'updateGroup',
    remove: 'deleteGroup',
});
attachReferenceTableContract(processIntegration, {
    fetch: 'fetchProcesses',
    create: 'createProcess',
    update: 'updateProcess',
    remove: 'deleteProcess',
    bulkRemove: 'bulkDeleteProcesses',
});
attachReferenceTableContract(threatIntegration, {
    fetch: 'fetchThreats',
    create: 'createThreat',
    update: 'updateThreat',
    remove: 'deleteThreat',
    bulkRemove: 'bulkDeleteThreats',
});
attachReferenceTableContract(rootCauseIntegration, {
    fetch: 'fetchRootCauses',
    create: 'createRootCause',
    update: 'updateRootCause',
    remove: 'deleteRootCause',
    bulkRemove: 'bulkDeleteRootCauses',
});
attachReferenceTableContract(classificationIntegration, {
    fetch: 'fetchClassification',
    create: 'createClassification',
    update: 'updateClassification',
    remove: 'deleteClassification',
    bulkRemove: 'bulkDeleteClassification',
});
attachReferenceTableContract(documentTypeIntegration, {
    fetch: 'fetchDocumentTypes',
    create: 'createDocumentType',
    update: 'updateDocumentType',
    remove: 'deleteDocumentType',
    bulkRemove: 'bulkDeleteDocumentTypes',
});
attachReferenceTableContract(teamIntegration, {
    fetch: 'fetchTeams',
    create: 'createTeam',
    update: 'updateTeam',
    remove: 'deleteTeam',
    bulkRemove: 'bulkDeleteTeams',
});
attachReferenceTableContract(companyIntegration, {
    create: 'create',
    update: 'update',
    remove: 'delete',
});
attachReferenceTableContract(applicationMethodIntegration, {
    fetch: 'fetchMethods',
    create: 'createMethod',
    update: 'updateMethod',
    remove: 'deleteMethod',
});
attachReferenceTableContract(applicationRequestInformationIntegration, {
    fetch: 'fetchAll',
    create: 'create',
    update: 'update',
    remove: 'deleteItem',
});
attachReferenceTableContract(explicitConsentMethodIntegration, {
    fetch: 'fetchExplicitConsentMethods',
    create: 'createExplicitConsentMethod',
    update: 'updateExplicitConsentMethod',
    remove: 'deleteExplicitConsentMethod',
});
attachReferenceTableContract(explicitConsentReasonIntegration, {
    fetch: 'fetchExplicitConsentReasons',
    create: 'createExplicitConsentReason',
    update: 'updateExplicitConsentReason',
    remove: 'deleteExplicitConsentReason',
});
attachReferenceTableContract(mailTemplateIntegration, {
    fetch: 'fetchMailTemplates',
    create: 'createMailTemplate',
    update: 'updateMailTemplate',
    remove: 'deleteMailTemplate',
    bulkRemove: 'bulkDeleteMailTemplates',
});
companyIntegration.fetchList = (page, pageSize, search, sortBy, sortOrder, columnFilters, extraQuery) =>
    companyIntegration.fetchAll({
        page,
        pageSize,
        search,
        sortBy,
        sortOrder,
        ...columnFilters,
        ...extraQuery,
    });

// Dedicated reference integrations also get mutation-level refresh safety-net.
attachReferenceMutationRefresh(unitIntegration, 'unit');
attachReferenceMutationRefresh(roleIntegration, 'role');
attachReferenceMutationRefresh(userIntegration, 'user');
attachReferenceMutationRefresh(companyIntegration, 'company');
attachReferenceMutationRefresh(teamIntegration, 'team');
attachReferenceMutationRefresh(processIntegration, 'process');
attachReferenceMutationRefresh(assetsIntegration, 'asset');
attachReferenceMutationRefresh(assetCategoryIntegration, 'assetCategory');
attachReferenceMutationRefresh(assetGroupIntegration, 'assetGroup');
attachReferenceMutationRefresh(documentTypeIntegration, 'documentType');
attachReferenceMutationRefresh(classificationIntegration, 'classification');
attachReferenceMutationRefresh(threatIntegration, 'threat');
attachReferenceMutationRefresh(weaknessIntegration, 'weakness');
attachReferenceMutationRefresh(vendorIntegration, 'vendor');
attachReferenceMutationRefresh(legalReasonIntegration, 'legalReason');
attachReferenceMutationRefresh(collectionMethodIntegration, 'collectionMethod');
attachReferenceMutationRefresh(processingPurposeIntegration, 'processingPurpose');
attachReferenceMutationRefresh(subjectPersonGroupIntegration, 'subjectPersonGroup');
attachReferenceMutationRefresh(personalDataCategoryIntegration, 'personalDataCategory');
attachReferenceMutationRefresh(ticketCategoryIntegration, 'ticketCategory');
attachReferenceMutationRefresh(ticketSlaIntegration, 'ticketSLA');
attachReferenceMutationRefresh(mailTemplateIntegration, 'mailTemplate');
attachReferenceMutationRefresh(rootCauseIntegration, 'rootCause');
attachReferenceMutationRefresh(applicationMethodIntegration, 'applicationMethod');
attachReferenceMutationRefresh(applicationRequestInformationIntegration, 'applicationRequestInformation');
attachReferenceMutationRefresh(explicitConsentMethodIntegration, 'explicitConsentMethod');
attachReferenceMutationRefresh(explicitConsentReasonIntegration, 'explicitConsentReason');
attachReferenceMutationRefresh(formTemplateIntegration, 'formTemplate');

// Simple reference table integrations already refresh via createSimpleTableIntegration
// (createRecord/updateRecord/removeRecord). Do not double-wrap — that orphaned skip tokens.

[
    [applicationTrackingIntegration, 'applicationTracking'],
    [explicitConsentIntegration, 'explicitConsent'],
    [kvkkCompanyIntegration, 'kvkkCompany'],
    [delegationIntegration, 'delegation'],
    [partyTemplateIntegration, 'partyTemplate'],
    [workflowTemplateIntegration, 'workflowTemplate'],
    [assignmentTemplateIntegration, 'assignmentTemplate'],
    [contextIntegration, 'context'],
    [communicationIntegration, 'communication'],
    [meetingIntegration, 'meeting'],
    [interestedPartyIntegration, 'interestedParty'],
    [scopeDocumentIntegration, 'scopeDocument'],
    [controlIntegration, 'control'],
    [objectiveIntegration, 'objective'],
    [competenceIntegration, 'competence'],
    [assessmentQuestionIntegration, 'assessmentQuestion'],
    [vulnerabilityIntegration, 'vulnerability'],
    [assessmentIntegration, 'assessment'],
    [auditPlanIntegration, 'auditPlan'],
    [incidentIntegration, 'incident'],
    [correctiveActionIntegration, 'action'],
    [changeRequestIntegration, 'changeRequest'],
    [problemIntegration, 'problem'],
    [serviceCatalogItemIntegration, 'serviceCatalogItem'],
    [biaIntegration, 'bia'],
    [bcpIntegration, 'bcp'],
    [crisisEventIntegration, 'crisisEvent'],
    [exercisesIntegration, 'exercise'],
    [trainingIntegration, 'training'],
    [documentIntegration, 'document'],
    [webhookSubscriptionIntegration, 'webhookSubscription'],
    [workflowInstanceIntegration, 'workflowInstance'],
    [pdInventoryIntegration, 'personalDataInventory'],
    [riskRegisterIntegration, 'riskRegister'],
    [riskTreatmentIntegration, 'riskTreatment'],
].forEach(([integration, entity]) => {
    attachReferenceMutationRefresh(integration, entity);
});

// Notification deep-link locate: attach locateRowInList to list integrations.
wireReferenceTableLocate({
    collectionMethodIntegration,
    legalReasonIntegration,
    processingPurposeIntegration,
    subjectPersonGroupIntegration,
    processIntegration,
    threatIntegration,
    rootCauseIntegration,
    classificationIntegration,
    documentTypeIntegration,
    teamIntegration,
    companyIntegration,
    applicationMethodIntegration,
    applicationRequestInformationIntegration,
    explicitConsentMethodIntegration,
    explicitConsentReasonIntegration,
    mailTemplateIntegration,
    personalDataCategoryIntegration,
    privacyRetentionTableIntegration,
    transferTypeTableIntegration,
    regulatorySourceIntegration,
    regulatoryClauseIntegration,
    complianceObligationIntegration,
    complianceFrameworkIntegration,
    dpiaTemplateIntegration,
    governanceReportDefinitionIntegration,
    internationalTransferTableIntegration,
    vendorAssessmentIntegration,
});

wireListNotificationLocate({
    assetsIntegration,
    riskRegisterIntegration,
    controlIntegration,
    documentIntegration,
    incidentIntegration,
    bcpIntegration,
    biaIntegration,
    crisisEventIntegration,
    vendorIntegration,
    meetingIntegration,
    exercisesIntegration,
    exerciseImprovementActionsIntegration,
    crisisRecoveryActivitiesIntegration,
    crisisImprovementActionsIntegration,
    riskTreatmentIntegration,
    applicationTrackingIntegration,
    explicitConsentIntegration,
    communicationIntegration,
    contextIntegration,
    interestedPartyIntegration,
    scopeDocumentIntegration,
    assessmentIntegration,
    assessmentQuestionIntegration,
    competenceIntegration,
    objectiveIntegration,
    vulnerabilityIntegration,
    trainingIntegration,
    changeRequestIntegration,
    problemIntegration,
    serviceCatalogItemIntegration,
    userIntegration,
    roleIntegration,
    delegationIntegration,
    assetGroupIntegration,
    assetCategoryIntegration,
    pdInventoryIntegration,
    workflowTemplateIntegration,
    partyTemplateIntegration,
    webhookSubscriptionIntegration,
    ticketIntegration,
    correctiveActionIntegration,
    auditEvaluationIntegration,
    assignmentTemplateIntegration,
    processIntegration,
    weaknessIntegration,
    formTemplateIntegration,
    surveyDispatchIntegration,
    cookieInventoryIntegration,
    dpaIntegration,
    disposalIntegration,
    dpiaAssessmentIntegration,
    consentRecordIntegration,
    privacyNoticeIntegration,
    dataBreachIntegration,
    dataSubjectRequestIntegration,
    integrationConnectionIntegration,
    bigdesRoadMapWorkPackageIntegration,
    bigdesCompensatoryControlIntegration,
});


export { httpGet, httpPost, httpPut, httpDelete } from './sdk-instance.js';
export { sdk };
export default sdk;
